import React, { useRef, useState } from 'react';
import { ArrowLeft, Camera, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PhoneFrame from '../components/PhoneFrame';
import StarField from '../components/StarField';
import { useApp } from '../context/AppContext';
import { uploadImage } from '../lib/orbitApi';

export default function EditProfile() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const { profile, saveProfile } = useApp();
  const [username, setUsername] = useState(profile?.username || '');
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [tagsText, setTagsText] = useState((profile?.tags || []).join(', '));
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      setAvatarUrl(await uploadImage(file));
      toast('Profile picture uploaded');
    } catch (err) {
      toast(err.message || 'Could not upload profile picture');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving || uploading) return;
    setSaving(true);
    try {
      await saveProfile({
        username,
        displayName,
        bio,
        tags: tagsText.split(','),
        avatarUrl,
      });
      toast('Profile updated');
      nav('/profile');
    } catch (err) {
      if (err.code === '23505' || /duplicate/i.test(err.message || '')) toast('That username is already taken');
      else toast(err.message || 'Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  const initial = (displayName || username || '?')[0]?.toUpperCase();

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10 pb-8">
          <header className="orbit-page-header">
            <button type="button" className="press orbit-icon-button" onClick={() => nav(-1)} aria-label="Go back">
              <ArrowLeft />
            </button>
            <h1 className="font-display orbit-page-title flex-1 text-center">Customize Profile</h1>
            <div className="w-10" aria-hidden="true" />
          </header>

          <form onSubmit={submit} className="max-w-xl mx-auto p-4 space-y-4">
            <section className="orbit-surface-card flex flex-col items-center gap-4">
              <div className="relative rounded-full p-[3px] glow-purple" style={{ background: 'linear-gradient(180deg,#8b7ce8,#4b3f8f)' }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile preview" className="w-32 h-32 rounded-full object-cover" style={{ border: '3px solid #0a0b1e' }} />
                ) : (
                  <div className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold" style={{ border: '3px solid #0a0b1e', background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)' }}>
                    {initial}
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadAvatar} />
              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" className="press orbit-primary-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Camera /> {uploading ? 'Uploading…' : 'Choose picture'}
                </button>
                {avatarUrl && (
                  <button type="button" className="press orbit-secondary-button" onClick={() => setAvatarUrl('')}>
                    <X /> Remove
                  </button>
                )}
              </div>
            </section>

            <label className="block">
              <span className="block text-sm font-semibold mb-2" style={{ color: '#c9bcf0' }}>Display name</span>
              <input className="orbit-field" style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff' }} value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={50} />
            </label>

            <label className="block">
              <span className="block text-sm font-semibold mb-2" style={{ color: '#c9bcf0' }}>Username</span>
              <input className="orbit-field" style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff' }} value={username} onChange={(event) => setUsername(event.target.value.toLowerCase())} maxLength={30} />
            </label>

            <label className="block">
              <span className="flex justify-between text-sm font-semibold mb-2" style={{ color: '#c9bcf0' }}>
                <span>Profile description</span><span>{bio.length}/160</span>
              </span>
              <textarea className="w-full min-h-28 p-4 rounded-2xl outline-none resize-y" style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff', border: '1px solid var(--orbit-border)' }} value={bio} onChange={(event) => setBio(event.target.value)} maxLength={160} placeholder="Tell the cosmos about yourself…" />
            </label>

            <label className="block">
              <span className="block text-sm font-semibold mb-2" style={{ color: '#c9bcf0' }}>Tags</span>
              <input className="orbit-field" style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff' }} value={tagsText} onChange={(event) => setTagsText(event.target.value)} placeholder="art, space, design" />
              <span className="block text-xs mt-2" style={{ color: '#8079a6' }}>Up to 8 comma-separated tags, 20 characters each.</span>
            </label>

            <button type="submit" className="press orbit-primary-button w-full" disabled={saving || uploading}>
              <Save /> {saving ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        </div>
      </div>
    </PhoneFrame>
  );
}
